import React, { createContext, useContext, ReactNode } from 'react';
import { createStoresWithStorage } from '../stores/childNavStores';
import { StorageInterface } from '../stores/childNavStores';

// Define the store context type
type StoresType = ReturnType<typeof createStoresWithStorage>;

// Cache the React context on globalThis so that Vite HMR module re-evaluations
// reuse the same context object rather than creating a new one. Without this,
// after any hot reload the StoreProvider hands a value to the OLD context while
// child components read from the NEW context → "useStores must be used within a
// StoreProvider" error at runtime.
const _g = globalThis as any;
if (!_g.__childNavStoreContext) {
  _g.__childNavStoreContext = createContext<StoresType | null>(null);
}
const StoreContext: React.Context<StoresType | null> = _g.__childNavStoreContext;

interface StoreProviderProps {
  children: ReactNode;
  storage: StorageInterface;
  apiService?: any; // ApiService type
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ 
  children, 
  storage, 
  apiService 
}) => {
  // Create stores with the provided storage
  const stores = React.useMemo(() => {
    const storeInstances = createStoresWithStorage(storage);
    
    // Set API service if provided
    if (apiService && storeInstances.setApiService) {
      storeInstances.setApiService(apiService);
    }
    
    return storeInstances;
  }, [storage, apiService]);

  // Initialize app on mount
  React.useEffect(() => {
    console.log('🔧 StoreProvider: Effect triggered, stores available:', !!stores);
    
    if (stores && (stores as any).executeBootSequence) {
      console.log('🔧 StoreProvider: Calling executeBootSequence(startup)...');
      (stores as any).executeBootSequence('startup').catch((error: unknown) => {
        console.error('StoreProvider: Failed to execute startup boot sequence:', error);
      });
    } else if (stores && stores.initializeApp) {
      // Backward compatibility while all callsites migrate to executeBootSequence.
      console.log('🔧 StoreProvider: executeBootSequence unavailable, falling back to initializeApp...');
      stores.initializeApp().catch((error: unknown) => {
        console.error('StoreProvider: Failed to initialize app:', error);
      });
    } else {
      console.log('🔧 StoreProvider: No stores or boot initialization method available');
    }
  }, [stores]);

  return (
    <StoreContext.Provider value={stores}>
      {children}
    </StoreContext.Provider>
  );
};

// Hooks to use the stores
export const useStores = (): StoresType => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStores must be used within a StoreProvider');
  }
  return context;
};

export const useProfileStore = () => {
  const stores = useStores();
  return stores.useProfileStore;
};

export const useUserStore = () => {
  const stores = useStores();
  return stores.useUserStore;
};

export const useMilestonesStore = () => {
  const stores = useStores();
  return stores.useMilestonesStore;
};

export const useConcernsStore = () => {
  const stores = useStores();
  return stores.useConcernsStore;
};

export const useWellVisitsStore = () => {
  const stores = useStores();
  return stores.useWellVisitsStore;
};

export const useActionsStore = () => {
  const stores = useStores();
  return stores.useActionsStore;
};

export const useContentStore = () => {
  const stores = useStores();
  return stores.useContentStore;
};